/*
 * NOOS ORDERING DASHBOARD (Max, 2026-10-27: "Làm module này lên portal nhé") — Young's NOOS ordering sheet as a portal
 * screen; the server half is noos.js. Four tabs:
 *   Dashboard          the sheet's four metrics for one stock day (REQ1 coverage by entity, REQ2 below coverage,
 *                      REQ3 reorder-point alerts incl. open PO / in transit, REQ4 excess) and the rows behind them,
 *                      with the portal's Excel sort / filter on every column
 *   Tier list          Miley's NOOS tiers (SAP code → NOOS / Semi-NOOS / EOL / On-Demand), editable by an admin
 *   Supply parameters  Linda's product-level lead time / MOQ / order cycle (read)
 *   Settings           the provisional numbers: targets, z, excess threshold, production + transit weeks
 * Staging only for now — behind REACT_APP_NOOS; the backend half is NOOS_MODULE=1. Grant noos_dashboard.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, Download, FileCode, Link2, RefreshCw, X } from 'lucide-react';
import Header from './Header';
import TabIcon from './TabIcon';
import ModuleHelp from './ModuleHelp';
import useAuth from './useAuth';
import { SortTh } from './15_SampleTracker';
import useColumnTools from './useColumnTools';
import { BACKEND_URL } from './backendConfig';
import './ProjectApproval.css';
import './Noos.css';
import './EusuStock.css'; // the share-link dialog is EUSU's, the same markup and classes

const API = `${BACKEND_URL}/api/noos`;
const ENTITIES = ['HINC', 'HUSA', 'HEMA', 'HPAC', 'HJPN'];
const TIERS = ['NOOS', 'Semi-NOOS', 'EOL', 'On-Demand'];
const call = async (method, url, body) => {
  const r = await fetch(`${API}${url}`, { method, credentials: 'include', headers: body ? { 'Content-Type': 'application/json' } : {}, body: body ? JSON.stringify(body) : undefined });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
  return j;
};
const n0 = (v) => (v === null || v === undefined || v === '' ? '' : Math.round(Number(v)).toLocaleString('en-US'));
const n1 = (v) => (v === null || v === undefined || v === '' ? '' : Number(v).toLocaleString('en-US', { maximumFractionDigits: 1, minimumFractionDigits: 1 }));
const n2 = (v) => (v === null || v === undefined || v === '' ? '' : Number(v).toLocaleString('en-US', { maximumFractionDigits: 2 }));

/* ---------------------------------------------------------------------------------------------------------------
   THE DASHBOARD AS YOUNG KNOWS IT — the web app on the sheet (Code.gs + Index.html, pasted by Max on 27/Oct): three
   assumption panels with sliders that recompute on the spot (nothing is saved — "Reset assumptions" puts the saved
   defaults back), multi-choice Entity and Tier filters (Tier starts on Core = NOOS + Semi-NOOS), four KPI cards that
   open their tab, and the five tabs Overview · Below Coverage · ROP Alerts · Excess · Review Data with the web app's
   default orders. Its rules, not the sheet's, where they differ: excess only where stock is on hand; "Need review" =
   no CV, no tier match, stock but no sell-out in 4 weeks (No sale 52W / Slow mover), no supply parameter. One change:
   the lead-time slider is the PRODUCTION lead time (+ each entity's transit weeks), because on the portal every row
   has a lead time — the web app's "fallback" slider would move nothing. */
const CORE = ['NOOS', 'Semi-NOOS'];
const isCore = (t) => CORE.includes(t);
const median = (a) => { if (!a.length) return null; const s = [...a].sort((x, y) => x - y); const m = Math.floor(s.length / 2); return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2; };

const computeRow = (r, a, transit) => {
  const target = r.tier === 'NOOS' ? a.noosTarget : r.tier === 'Semi-NOOS' ? a.semiTarget : null;
  const z = r.tier === 'NOOS' ? a.noosZ : r.tier === 'Semi-NOOS' ? a.semiZ : null;
  const avg = Number(r.avg4) || 0; const onHand = Number(r.onHand) || 0;
  const coverage = avg > 0 ? onHand / avg : null;
  const cv = r.cv === null || r.cv === undefined ? null : Number(r.cv);
  const hasCv = cv !== null && cv > 0;
  const lt = Number(a.prodLt) + Number(transit[r.entity] ?? 0);
  const open = Number(r.open) || 0; const supply = onHand + open;
  const canRop = isCore(r.tier) && avg > 0 && hasCv && lt > 0 && z > 0;
  const std = canRop ? avg * cv : null;
  const ss = canRop ? Math.ceil(Number((z * std * Math.sqrt(lt)).toFixed(9))) : null;
  const rop = canRop ? Math.ceil(Number((avg * lt + ss).toFixed(9))) : null;
  const gap = canRop ? Math.max(0, rop - supply) : null;
  const dead52 = r.q52 === null || r.q52 === undefined || Number(r.q52) === 0;
  const noRecent = onHand > 0 && avg === 0;
  /* more came back than went out — real customer returns (HEMA 10000039: Merkkur sent 122 back on 04/Nov/2025), not
     a data error, and not a "slow mover" either */
  const netReturns = Number(r.q52) < 0 || Number(r.q4) < 0;
  return {
    ...r, target, z, avg, onHand, coverage, cv, lt, open, supply, ss, rop, gap,
    below: isCore(r.tier) && coverage !== null && target !== null && coverage < target,
    ropAlert: canRop && supply <= rop,
    excess: coverage !== null && coverage > a.excessWeeks && onHand > 0,
    over: coverage !== null ? coverage - a.excessWeeks : null,
    dead52, noRecent, slowMover: noRecent && !dead52 && !netReturns, netReturns,
    needCv: isCore(r.tier) && avg > 0 && !hasCv,
    noTier: r.match === 'No tier match' || r.tier === 'Unclassified',
    needSupply: isCore(r.tier) && !(Number(r.moq) > 0),
  };
};

/* one multi-choice dropdown, the web app's Entity / Tier filter */
function MultiFilter({ label, options, picked, setPicked, extra, testid }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = React.useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);
  return (
    <div className={`nz-filter ${open ? 'open' : ''}`} ref={ref}>
      <button type="button" className="nz-filter-btn" onClick={() => setOpen((o) => !o)} data-nz-filter={testid}><span>{label}</span><span>▾</span></button>
      {open && (
        <div className="nz-filter-menu">
          <div className="nz-filter-actions">
            <button type="button" onClick={() => setPicked(new Set(options))}>{t('xNoos.fAll')}</button>
            {extra || <button type="button" onClick={() => setPicked(new Set())}>{t('xNoos.fNone')}</button>}
          </div>
          {options.map((o) => (
            <label key={o} className="nz-filter-opt">
              <input type="checkbox" checked={picked.has(o)} onChange={() => setPicked((s) => { const n = new Set(s); if (n.has(o)) n.delete(o); else n.add(o); return n; })} data-nz-opt={o} /> {o}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

function Slider({ id, label, hint, min, max, step, a, setA }) {
  return (
    <div className="nz-control">
      <div>
        <label htmlFor={`nz-${id}`}>{label}</label>
        <input type="range" min={min} max={max} step={step} value={a[id]} onChange={(e) => setA({ ...a, [id]: Number(e.target.value) })} data-nz-range={id} />
        {hint && <div className="nz-hintbox">{hint}</div>}
      </div>
      <input id={`nz-${id}`} type="number" min={min} max={max} step={step} value={a[id]} onChange={(e) => setA({ ...a, [id]: e.target.value === '' ? '' : Number(e.target.value) })} data-nz-num={id} />
    </div>
  );
}

const Pill = ({ tier }) => <span className={`nz-tier nz-tier-${String(tier).toLowerCase()}`}>{tier}</span>;
const Sku = ({ r }) => <div><div className="nz-prod">{r.product || r.sap}</div><div className="nz-sub">{r.sap}{r.color ? ` · ${r.color}` : ''}</div></div>;
const Sig = ({ kind, children }) => <span className={`nz-flag nz-flag-${kind}`}>{children}</span>;

/* a list table: No column, the portal's Excel sort / filter on every column, the web app's default order */
function ListTable({ rows, cols, defSort, testid, cap = 1000 }) {
  const { t } = useTranslation();
  const sorted = useMemo(() => {
    if (!defSort) return rows;
    const [k, dir] = defSort; const f = cols.find((c) => c.key === k)?.get;
    return [...rows].sort((x, y) => { const a = f(x); const b = f(y); const ea = a === null || a === '' ; const eb = b === null || b === '';
      if (ea || eb) return ea && eb ? 0 : ea ? 1 : -1; return (typeof a === 'number' ? a - b : String(a).localeCompare(String(b))) * (dir === 'desc' ? -1 : 1); });
  }, [rows, cols, defSort]);
  const tools = useColumnTools(sorted, cols.filter((c) => c.get));
  const shown = tools.shown.slice(0, cap);
  return (
    <>
      <div className="nz-listbar"><span className="nz-count">{t('xNoos.shown', { n: tools.shown.length, total: rows.length })}</span>
        {tools.active && <button type="button" className="pa-btn" onClick={tools.clear}>{t('xNoos.clear')}</button>}</div>
      <div className="pa-table-wrap nz-scroll">
        <table className="pa-table nz-table" data-nz-list={testid}>
          <thead><tr>
            <th className="num">{t('xNoos.colNo')}</th>
            {cols.map((c) => (c.get ? <SortTh key={c.key} label={c.label} className={c.num ? 'num' : ''} {...tools.thProps(c.key)} /> : <th key={c.key}>{c.label}</th>))}
          </tr></thead>
          <tbody>
            {!shown.length && <tr><td colSpan={cols.length + 1} className="nz-empty">{t('xNoos.noRows')}</td></tr>}
            {shown.map((r, i) => (
              <tr key={`${r.entity}|${r.sap}`}>
                <td className="num nz-no">{i + 1}</td>
                {cols.map((c) => <td key={c.key} className={c.num ? 'num' : ''}>{c.render ? c.render(r) : c.get(r)}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {tools.shown.length > cap && <p className="nz-hint">{t('xNoos.capped', { cap: n0(cap), n: n0(tools.shown.length) })}</p>}
    </>
  );
}

/* where each entity's numbers come from and as of when (ES Kim, 27/Oct: update process, HPAC sell-out, open PO dates).
   ONE LINE UNTIL ASKED (Max, 28/10: "nhiều chữ quá … làm loãng sự tập trung"): the dates and any warning in a strip, the
   table behind "Details" — and in the table the open PO as columns of numbers rather than a sentence per company. The
   open / shut choice is remembered per viewer. */
const SRC_OPEN_KEY = 'noos.sourcesOpen';
function Sources({ data }) {
  const { t } = useTranslation();
  const FROM = { sap: 'SAP', netsuite: 'NetSuite', file: t('xNoos.srcFile'), legacy: t('xNoos.srcFile') };
  const [open, setOpen] = useState(() => { try { return localStorage.getItem(SRC_OPEN_KEY) === '1'; } catch (e) { return false; } });
  const toggle = () => setOpen((o) => { try { localStorage.setItem(SRC_OPEN_KEY, o ? '0' : '1'); } catch (e) { /* private window */ } return !o; });
  const src = data.sources || [];
  const staleStock = (s) => s.stockDay !== data.day;
  const staleSales = (s) => !s.salesTo || s.salesTo < data.weeks.last4[1];
  const warns = src.filter((s) => staleStock(s) || staleSales(s)).map((s) => s.entity);
  const readAt = data.openPoReadAt || data.openPoSapAt;
  const salesTo = src.map((s) => s.salesTo).filter(Boolean).sort()[0];
  const nz = (v) => (v ? n0(v) : '—');
  return (
    <section className={`nz-panel nz-sources ${open ? 'open' : ''}`} data-nz-sources>
      <div className="nz-src-strip">
        <h2>{t('xNoos.srcTitle')}</h2>
        <span>{t('xNoos.srcSumStock', { day: data.day })}</span>
        <span>{t('xNoos.srcSumSales', { day: salesTo || '—' })}</span>
        <span>{readAt ? t('xNoos.srcSumOp', { at: String(readAt).replace('T', ' ').slice(0, 16) }) : t('xNoos.srcSumNoOp')}</span>
        {warns.length > 0 && <span className="nz-stale" data-nz-srcwarn>⚠ {t('xNoos.srcSumWarn', { list: warns.join(', ') })}</span>}
        {!!data.openPoNote && <span className="nz-stale">⚠ {t('xNoos.srcOpFailed', { why: data.openPoNote })}</span>}
        <button type="button" className="pa-btn nz-src-btn" onClick={toggle} aria-expanded={open} data-nz-srctoggle>
          {open ? t('xNoos.srcHide') : t('xNoos.srcDetails')}
        </button>
      </div>
      {open && (
        <>
          <div className="pa-table-wrap">
            <table className="pa-table nz-table nz-src-table">
              <thead>
                <tr>
                  <th rowSpan={2}>{t('xNoos.colEntity')}</th><th rowSpan={2}>{t('xNoos.srcStock')}</th><th rowSpan={2}>{t('xNoos.srcSales')}</th>
                  <th colSpan={6} className="grp">{t('xNoos.srcTransit')}</th>
                </tr>
                <tr>
                  <th>{t('xNoos.srcColFrom')}</th><th className="num">{t('xNoos.srcColCome')}</th><th className="num">{t('xNoos.srcColGroup')}</th>
                  <th className="num">{t('xNoos.srcColPassed')}</th><th className="num">{t('xNoos.srcColPast')}</th><th className="num">{t('xNoos.srcColTo')}</th>
                </tr>
              </thead>
              <tbody>
                {src.map((s) => (
                  <tr key={s.entity} data-nz-source={s.entity}>
                    <td><b>{s.entity}</b></td>
                    <td className={staleStock(s) ? 'nz-stale' : ''}>
                      {!s.stockFrom ? t('xNoos.srcNoStock') : s.stockFrom === 'carry'
                        ? t('xNoos.srcCarry', { day: s.stockDay || '?', name: s.fileName || '' })
                        : `${FROM[s.stockFrom] || s.stockFrom} · ${s.stockDay}`}
                    </td>
                    <td className={staleSales(s) ? 'nz-stale' : ''}>{s.salesTo || t('xNoos.srcNoSales')}</td>
                    {s.supplyBasis === 'open_po' ? (
                      <>
                        <td data-nz-supply="open_po">{s.opSource} · {t('xNoos.srcLines', { n: n0(s.opLines || 0) })}</td>
                        <td className="num"><b>{n0(s.opQty || 0)}</b></td>
                        <td className="num">{nz(s.opFromGroup)}</td>
                        <td className="num">{s.opToGroup ? `− ${n0(s.opToGroup)}` : '—'}</td>
                        <td className="num nz-dim">{nz(s.opPastDue)}</td>
                        <td className="num">{nz(s.toQty)}</td>
                      </>
                    ) : (
                      <td colSpan={6} className={s.transitItems ? 'nz-stale' : 'nz-dim'} data-nz-supply={s.supplyBasis}>
                        {s.transitItems ? t('xNoos.srcTransitOnly', { n: n0(s.transitItems), q: n0(s.transitQty) })
                          : t(s.stockFrom === 'netsuite' ? 'xNoos.srcNoneOpen' : s.entity === 'HINC' ? 'xNoos.srcNoTransitHinc' : 'xNoos.srcNoTransit')}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="nz-hintbox">{readAt ? t('xNoos.srcNoteShort') : t('xNoos.srcNoteNoOp')}</p>
        </>
      )}
    </section>
  );
}

function Dashboard({ data, head }) {
  const { t } = useTranslation();
  const defaults = useMemo(() => ({ noosTarget: data.settings.targetNoos, semiTarget: data.settings.targetSemi, prodLt: data.settings.prodLt,
    noosZ: data.settings.zNoos, semiZ: data.settings.zSemi, excessWeeks: data.settings.excessWeeks }), [data]);
  const [a, setA] = useState(defaults);
  useEffect(() => { setA(defaults); }, [defaults]);
  const entityOpts = useMemo(() => [...new Set(data.rows.map((r) => r.entity))].sort(), [data]);
  const tierOpts = useMemo(() => { const all = [...new Set(data.rows.map((r) => r.tier))]; return [...CORE, ...all.filter((x) => !isCore(x)).sort()]; }, [data]);
  const [ents, setEnts] = useState(() => new Set(entityOpts));
  const [tiers, setTiers] = useState(() => new Set(CORE));
  const [sub, setSub] = useState('overview');
  const num = (v) => (Number.isFinite(Number(v)) && v !== '' ? Number(v) : 0);
  const aa = useMemo(() => Object.fromEntries(Object.entries(a).map(([k, v]) => [k, num(v)])), [a]);
  const rows = useMemo(() => data.rows.filter((r) => ents.has(r.entity) && tiers.has(r.tier)).map((r) => computeRow(r, aa, data.settings.transit || {})), [data, ents, tiers, aa]);
  const below = rows.filter((r) => r.below); const rop = rows.filter((r) => r.ropAlert); const excess = rows.filter((r) => r.excess);
  /* one line per row with its first issue — the web app's order (its review table came truncated in the paste, so the
     issue it shows when a row has several is our reading of it), plus net returns, which ES Kim asked about (27/Oct) —
     first, because it is what explains an odd-looking number; most of those rows also have no tier */
  const review = useMemo(() => rows.filter((r) => r.needCv || r.noRecent || r.noTier || r.needSupply || r.netReturns).map((r) => {
    const [issue, note] = r.netReturns ? ['isReturns', 'noteReturns'] : r.needCv ? ['isCv', 'noteCv'] : r.noTier ? ['isTier', 'noteTier']
      : r.noRecent && r.dead52 ? ['isDead', 'noteDead']
      : r.slowMover ? ['isSlow', 'noteSlow'] : ['isSupply', 'noteSupply'];
    return { ...r, issue: t(`xNoos.${issue}`), note: t(`xNoos.${note}`) };
  }), [rows, t]);
  const n0x = (v) => (v === null || v === undefined ? '' : n0(v));
  const entityRows = entityOpts.filter((e) => ents.has(e)).map((e) => {
    const it = rows.filter((r) => r.entity === e);
    return { entity: e, sap: e, rows: it.length, medianCover: median(it.map((r) => r.coverage).filter((x) => x !== null)), below: it.filter((r) => r.below).length,
      rop: it.filter((r) => r.ropAlert).length, excess: it.filter((r) => r.excess).length };
  });
  const tierRows = ['NOOS', 'Semi-NOOS', 'On-Demand', 'EOL', 'Unclassified'].map((tr) => {
    const it = rows.filter((r) => r.tier === tr);
    return { tier: tr, entity: tr, sap: tr, rows: it.length, below: it.filter((r) => r.below).length, rop: it.filter((r) => r.ropAlert).length,
      excess: it.filter((r) => r.excess).length, needCv: it.filter((r) => r.needCv).length };
  }).filter((x) => x.rows > 0);
  const entLabel = ents.size === entityOpts.length ? t('xNoos.allEntities') : ents.size === 0 ? t('xNoos.noEntities') : t('xNoos.nEntities', { n: ents.size });
  const coreOnly = tiers.size === 2 && CORE.every((x) => tiers.has(x));
  const tierLabel = tiers.size === tierOpts.length ? t('xNoos.allTiers') : coreOnly ? 'NOOS + Semi-NOOS' : tiers.size === 0 ? t('xNoos.noTiers') : t('xNoos.nTiers', { n: tiers.size });
  const reset = () => { setA(defaults); setEnts(new Set(entityOpts)); setTiers(new Set(CORE)); };
  const colsSku = [{ key: 'tier', label: t('xNoos.colTier'), get: (r) => r.tier, render: (r) => <Pill tier={r.tier} /> },
    { key: 'product', label: t('xNoos.colSkuProduct'), get: (r) => r.product || r.sap, render: (r) => <Sku r={r} /> },
    { key: 'entity', label: t('xNoos.colEntity'), get: (r) => r.entity }];
  const SUBS = [['overview', t('xNoos.sOverview')], ['below', t('xNoos.sBelow')], ['rop', t('xNoos.sRop')], ['excess', t('xNoos.sExcess')], ['review', t('xNoos.sReview')]];
  return (
    <>
      {/* the page head and the filters stay put while the sheet scrolls (Max, 28/10: "freeze / stick phần này lại") */}
      <div className="nz-head" data-nz-head>
      {head}
      <div className="nz-topbar">
        <div className="nz-actions">
          <button type="button" className="pa-btn" onClick={reset} data-nz-reset>{t('xNoos.resetAssumptions')}</button>
          <MultiFilter label={entLabel} options={entityOpts} picked={ents} setPicked={setEnts} testid="entity" />
          <MultiFilter label={tierLabel} options={tierOpts} picked={tiers} setPicked={setTiers} testid="tier"
            extra={<button type="button" onClick={() => setTiers(new Set(CORE))}>{t('xNoos.fCore')}</button>} />
        </div>
        <div className="nz-status">
          <div>{t('xNoos.refreshed')} {String(data.builtAt).replace('T', ' ').slice(0, 16)} UTC · {t('xNoos.stockDayShort', { day: data.day, week: data.weekCode })}</div>
          <div data-nz-weeks>{t('xNoos.selloutWeeks', { a: data.weeks.last4[0], b: data.weeks.last4[1], c: data.weeks.last52[0], d: data.weeks.last52[1] })}</div>
        </div>
      </div>
      </div>
      <Sources data={data} />

      <section className="nz-layout">
        <div className="nz-panel">
          <h2>{t('xNoos.p1')}</h2>
          <Slider id="noosTarget" label={t('xNoos.aNoosTarget')} hint={t('xNoos.hNoosTarget')} min={1} max={20} step={0.5} a={a} setA={setA} />
          <Slider id="semiTarget" label={t('xNoos.aSemiTarget')} hint={t('xNoos.hSemiTarget')} min={1} max={20} step={0.5} a={a} setA={setA} />
        </div>
        <div className="nz-panel">
          <h2>{t('xNoos.p2')}</h2>
          <Slider id="prodLt" label={t('xNoos.aProdLt')} hint={t('xNoos.hProdLt', { tr: Object.entries(data.settings.transit || {}).map(([k, v]) => `${k} ${v}`).join(' · ') })} min={1} max={30} step={0.5} a={a} setA={setA} />
          <Slider id="noosZ" label={t('xNoos.aNoosZ')} hint={t('xNoos.hNoosZ')} min={0.5} max={3.5} step={0.01} a={a} setA={setA} />
          <Slider id="semiZ" label={t('xNoos.aSemiZ')} hint={t('xNoos.hSemiZ')} min={0.5} max={3.5} step={0.01} a={a} setA={setA} />
        </div>
        <div className="nz-panel">
          <h2>{t('xNoos.p3')}</h2>
          <Slider id="excessWeeks" label={t('xNoos.aExcess')} hint={t('xNoos.hExcess')} min={4} max={52} step={0.5} a={a} setA={setA} />
          <div className="nz-hintbox">{t('xNoos.formula')}</div>
        </div>
      </section>

      <section className="nz-kpis">
        <button type="button" className="nz-kpi" onClick={() => setSub('below')} data-nz-kpi="below"><div className="nz-kpi-l">{t('xNoos.kBelow')}</div><div className="nz-kpi-v">{n0(below.length)} <small>{t('xNoos.rowsWord')}</small></div><div className="nz-kpi-n">{t('xNoos.kBelowN')}</div></button>
        <button type="button" className="nz-kpi" onClick={() => setSub('rop')} data-nz-kpi="rop"><div className="nz-kpi-l">{t('xNoos.kRop')}</div><div className="nz-kpi-v">{n0(rop.length)} <small>SKUs</small></div><div className="nz-kpi-n">{t('xNoos.kRopN')}</div></button>
        <button type="button" className="nz-kpi" onClick={() => setSub('excess')} data-nz-kpi="excess"><div className="nz-kpi-l">{t('xNoos.kExcess')}</div><div className="nz-kpi-v">{n0(excess.length)} <small>SKUs</small></div><div className="nz-kpi-n">{t('xNoos.kExcessN')}</div></button>
        <button type="button" className="nz-kpi" onClick={() => setSub('review')} data-nz-kpi="review"><div className="nz-kpi-l">{t('xNoos.kReview')}</div><div className="nz-kpi-v">{n0(review.length)} <small>{t('xNoos.rowsWord')}</small></div><div className="nz-kpi-n">{t('xNoos.kReviewN')}</div></button>
      </section>

      <nav className="nz-subtabs">
        {SUBS.map(([k, l]) => <button key={k} type="button" className={`nz-subtab ${sub === k ? 'active' : ''}`} onClick={() => setSub(k)} data-nz-sub={k}>{l}</button>)}
      </nav>

      {sub === 'overview' && (
        <div className="nz-grid2">
          <div className="nz-panel">
            <h2>{t('xNoos.oEntity')}</h2>
            <ListTable testid="entity" rows={entityRows} defSort={['entity', 'asc']} cols={[
              { key: 'entity', label: t('xNoos.colEntity'), get: (r) => r.entity, render: (r) => <b>{r.entity}</b> },
              { key: 'rows', label: t('xNoos.colRows'), num: true, get: (r) => r.rows, render: (r) => n0(r.rows) },
              { key: 'medianCover', label: t('xNoos.colMedian'), num: true, get: (r) => r.medianCover, render: (r) => n1(r.medianCover) },
              { key: 'below', label: t('xNoos.colBelow'), num: true, get: (r) => r.below },
              { key: 'rop', label: 'ROP', num: true, get: (r) => r.rop },
              { key: 'excess', label: t('xNoos.colExcess'), num: true, get: (r) => r.excess }]} />
          </div>
          <div className="nz-panel">
            <h2>{t('xNoos.oTier')}</h2>
            <ListTable testid="tier" rows={tierRows} cols={[
              { key: 'tier', label: t('xNoos.colTier'), get: (r) => r.tier, render: (r) => <Pill tier={r.tier} /> },
              { key: 'rows', label: t('xNoos.colRows'), num: true, get: (r) => r.rows, render: (r) => n0(r.rows) },
              { key: 'below', label: t('xNoos.colBelow'), num: true, get: (r) => r.below },
              { key: 'rop', label: 'ROP', num: true, get: (r) => r.rop },
              { key: 'excess', label: t('xNoos.colExcess'), num: true, get: (r) => r.excess },
              { key: 'needCv', label: t('xNoos.colNeedCv'), num: true, get: (r) => r.needCv }]} />
          </div>
        </div>
      )}
      {sub === 'below' && (
        <div className="nz-panel"><h2>{t('xNoos.hdBelow')}</h2>
          <ListTable testid="below" rows={below} defSort={['coverage', 'asc']} cols={[...colsSku,
            { key: 'onHand', label: t('xNoos.colOnHandPcs'), num: true, get: (r) => r.onHand, render: (r) => n0(r.onHand) },
            { key: 'avg', label: t('xNoos.colAvgPcs'), num: true, get: (r) => r.avg, render: (r) => n2(r.avg) },
            { key: 'coverage', label: t('xNoos.colCoverageWks'), num: true, get: (r) => r.coverage, render: (r) => <b>{n1(r.coverage)}</b> },
            { key: 'target', label: t('xNoos.colTargetWks'), num: true, get: (r) => r.target },
            { key: 'signal', label: t('xNoos.colSignal'), get: () => t('xNoos.sigBelow'), render: () => <Sig kind="y">{t('xNoos.sigBelow')}</Sig> }]} />
        </div>
      )}
      {sub === 'rop' && (
        <div className="nz-panel"><h2>{t('xNoos.hdRop')}</h2>
          <ListTable testid="rop" rows={rop} defSort={['gap', 'desc']} cols={[...colsSku,
            { key: 'onHand', label: t('xNoos.colOnHandPcs'), num: true, get: (r) => r.onHand, render: (r) => n0(r.onHand) },
            { key: 'open', label: t('xNoos.colOpenPcs'), num: true, get: (r) => r.open, render: (r) => n0(r.open) },
            { key: 'supply', label: t('xNoos.colSupplyPcs'), num: true, get: (r) => r.supply, render: (r) => n0(r.supply) },
            { key: 'rop', label: t('xNoos.colRopPcs'), num: true, get: (r) => r.rop, render: (r) => n0x(r.rop) },
            { key: 'gap', label: t('xNoos.colGapPcs'), num: true, get: (r) => r.gap, render: (r) => <b>{n0x(r.gap)}</b> },
            { key: 'lt', label: t('xNoos.colLt'), num: true, get: (r) => r.lt },
            { key: 'cv', label: 'CV', num: true, get: (r) => r.cv },
            { key: 'signal', label: t('xNoos.colSignal'), get: () => t('xNoos.sigReorder'), render: () => <Sig kind="y">{t('xNoos.sigReorder')}</Sig> }]} />
        </div>
      )}
      {sub === 'excess' && (
        <div className="nz-panel"><h2>{t('xNoos.hdExcess')}</h2>
          <ListTable testid="excess" rows={excess} defSort={['coverage', 'desc']} cols={[...colsSku,
            { key: 'onHand', label: t('xNoos.colOnHandPcs'), num: true, get: (r) => r.onHand, render: (r) => n0(r.onHand) },
            { key: 'avg', label: t('xNoos.colAvgPcs'), num: true, get: (r) => r.avg, render: (r) => n2(r.avg) },
            { key: 'coverage', label: t('xNoos.colCoverageWks'), num: true, get: (r) => r.coverage, render: (r) => <b>{n1(r.coverage)}</b> },
            { key: 'over', label: t('xNoos.colOver'), num: true, get: (r) => r.over, render: (r) => n1(r.over) },
            { key: 'signal', label: t('xNoos.colSignal'), get: () => t('xNoos.sigExcess'), render: () => <Sig kind="a">{t('xNoos.sigExcess')}</Sig> }]} />
        </div>
      )}
      {sub === 'review' && (
        <div className="nz-panel"><h2>{t('xNoos.hdReview')}</h2>
          <ListTable testid="review" rows={review} defSort={['issue', 'asc']} cols={[
            { key: 'issue', label: t('xNoos.colIssue'), get: (r) => r.issue, render: (r) => <Sig kind="o">{r.issue}</Sig> },
            { key: 'product', label: t('xNoos.colSkuProduct'), get: (r) => r.product || r.sap, render: (r) => <Sku r={r} /> },
            { key: 'entity', label: t('xNoos.colEntity'), get: (r) => r.entity },
            { key: 'tier', label: t('xNoos.colTier'), get: (r) => r.tier, render: (r) => <Pill tier={r.tier} /> },
            { key: 'onHand', label: t('xNoos.colOnHandPcs'), num: true, get: (r) => r.onHand, render: (r) => n0(r.onHand) },
            { key: 'avg', label: t('xNoos.colAvgPcs'), num: true, get: (r) => r.avg, render: (r) => n2(r.avg) },
            { key: 'note', label: t('xNoos.colNote'), get: (r) => r.note }]} />
        </div>
      )}
      <p className="nz-foot">{t('xNoos.foot', { a: aa.noosTarget, b: aa.semiTarget, c: aa.prodLt, d: aa.noosZ, e: aa.semiZ, f: aa.excessWeeks })}</p>
    </>
  );
}

function TierList({ canEdit }) {
  const { t } = useTranslation();
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState('');
  const [q, setQ] = useState('');
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => call('GET', '/tiers').then((r) => setRows(r.tiers)).catch((e) => setErr(e.message)), []);
  useEffect(() => { load(); }, [load]);
  const shownBase = useMemo(() => (rows || []).filter((r) => !q || `${r.sap} ${r.product} ${r.color} ${r.tier}`.toLowerCase().includes(q.toLowerCase())), [rows, q]);
  const tools = useColumnTools(shownBase, [
    { key: 'sap', get: (r) => r.sap || '' }, { key: 'product', get: (r) => r.product || '' }, { key: 'color', get: (r) => r.color || '' },
    { key: 'tier', get: (r) => r.tier }, { key: 'target', get: (r) => (r.target_weeks === null ? '' : Number(r.target_weeks)) },
    { key: 'cv', get: (r) => (r.normalized_cv === null ? '' : Number(r.normalized_cv)) }, { key: 'active', get: (r) => (r.active ? 'Y' : 'N') },
    { key: 'owner', get: (r) => r.owner || '' },
  ]);
  const save = async () => {
    try {
      if (edit.new) await call('POST', '/tiers', { sap: edit.sap, product: edit.product, color: edit.color, tier: edit.tier, targetWeeks: edit.target, cv: edit.cv });
      else await call('PUT', `/tiers/${edit.pk}`, { tier: edit.tier, targetWeeks: edit.target, cv: edit.cv, active: edit.active, notes: edit.notes });
      setEdit(null); load();
    } catch (e) { setEdit((x) => ({ ...x, err: e.message })); }
  };
  return (
    <>
      <p className="nz-basis">{t('xNoos.tierSub')}</p>
      {err && <div className="pa-banner err">{err}</div>}
      <div className="nz-listbar">
        <input className="pa-search" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('xNoos.search')} data-nz-tiersearch />
        <span className="nz-count">{t('xNoos.shown', { n: tools.shown.length, total: (rows || []).length })}</span>
        {canEdit && <button type="button" className="pa-btn primary" onClick={() => setEdit({ new: true, sap: '', product: '', color: '', tier: 'NOOS', target: '', cv: '' })} data-nz-addtier>{t('xNoos.addTier')}</button>}
      </div>
      {edit && (
        <div className="nz-edit" data-nz-edit>
          {edit.new ? (
            <>
              <label>SAP<input value={edit.sap} onChange={(e) => setEdit({ ...edit, sap: e.target.value })} data-nz-esap /></label>
              <label>{t('xNoos.colProduct')}<input value={edit.product} onChange={(e) => setEdit({ ...edit, product: e.target.value })} /></label>
              <label>{t('xNoos.colColor')}<input value={edit.color} onChange={(e) => setEdit({ ...edit, color: e.target.value })} /></label>
            </>
          ) : <span className="nz-edit-who"><b>{edit.sap}</b> {edit.product} {edit.color}</span>}
          <label>{t('xNoos.colTier')}<select value={edit.tier} onChange={(e) => setEdit({ ...edit, tier: e.target.value })} data-nz-etier>{TIERS.map((x) => <option key={x}>{x}</option>)}</select></label>
          <label>{t('xNoos.colTarget')}<input type="number" min="0" step="0.5" value={edit.target ?? ''} onChange={(e) => setEdit({ ...edit, target: e.target.value })} placeholder={t('xNoos.default')} /></label>
          <label>CV<input type="number" min="0" step="0.01" value={edit.cv ?? ''} onChange={(e) => setEdit({ ...edit, cv: e.target.value })} data-nz-ecv /></label>
          {!edit.new && <label className="nz-check"><input type="checkbox" checked={edit.active} onChange={(e) => setEdit({ ...edit, active: e.target.checked })} /> {t('xNoos.active')}</label>}
          <button type="button" className="pa-btn primary" onClick={save} data-nz-esave>{t('xNoos.save')}</button>
          <button type="button" className="pa-btn" onClick={() => setEdit(null)}>{t('xNoos.cancel')}</button>
          {edit.err && <span className="nz-err">{edit.err}</span>}
        </div>
      )}
      <div className="pa-table-wrap nz-scroll">
        <table className="pa-table nz-table" data-nz-tiers>
          <thead><tr>
            <SortTh label="SAP" {...tools.thProps('sap')} /><SortTh label={t('xNoos.colProduct')} {...tools.thProps('product')} /><SortTh label={t('xNoos.colColor')} {...tools.thProps('color')} />
            <SortTh label={t('xNoos.colTier')} {...tools.thProps('tier')} /><SortTh label={t('xNoos.colTarget')} className="num" {...tools.thProps('target')} />
            <SortTh label="CV" className="num" {...tools.thProps('cv')} /><SortTh label={t('xNoos.active')} {...tools.thProps('active')} /><SortTh label={t('xNoos.owner')} {...tools.thProps('owner')} />
            {canEdit && <th>{t('xNoos.action')}</th>}
          </tr></thead>
          <tbody>
            {tools.shown.map((r) => (
              <tr key={r.tier_pk} className={r.active ? '' : 'nz-off'}>
                <td className="nz-mono">{r.sap}</td><td>{r.product}</td><td>{r.color}</td><td><span className={`nz-tier nz-tier-${String(r.tier).toLowerCase()}`}>{r.tier}</span></td>
                <td className="num">{r.target_weeks === null ? '' : Number(r.target_weeks)}</td><td className="num">{r.normalized_cv === null ? <span className="nz-need">{t('xNoos.needCv')}</span> : Number(r.normalized_cv)}</td>
                <td>{r.active ? 'Y' : 'N'}</td><td>{r.owner}</td>
                {canEdit && <td><button type="button" className="pa-btn nz-b-edit" onClick={() => setEdit({ pk: r.tier_pk, sap: r.sap, product: r.product, color: r.color, tier: r.tier, target: r.target_weeks === null ? '' : Number(r.target_weeks), cv: r.normalized_cv === null ? '' : Number(r.normalized_cv), active: !!r.active, notes: r.notes || '' })} data-nz-edittier={r.tier_pk}>{t('xNoos.edit')}</button></td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function SupplyList() {
  const { t } = useTranslation();
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => { call('GET', '/supply').then((r) => setRows(r.supply)).catch((e) => setErr(e.message)); }, []);
  const tools = useColumnTools(rows || [], [
    { key: 'product', get: (r) => r.product }, { key: 'scope', get: (r) => r.scope }, { key: 'lt', get: (r) => Number(r.lt_with_frame_wks ?? '') },
    { key: 'lt2', get: (r) => Number(r.lt_no_frame_wks ?? '') }, { key: 'moq', get: (r) => Number(r.moq ?? '') }, { key: 'cycle', get: (r) => Number(r.order_cycle_wks ?? '') },
    { key: 'life', get: (r) => r.lifecycle || '' },
  ]);
  return (
    <>
      <p className="nz-basis">{t('xNoos.supplySub')}</p>
      {err && <div className="pa-banner err">{err}</div>}
      <div className="pa-table-wrap nz-scroll">
        <table className="pa-table nz-table" data-nz-supply>
          <thead><tr>
            <SortTh label={t('xNoos.colProduct')} {...tools.thProps('product')} /><SortTh label={t('xNoos.scope')} {...tools.thProps('scope')} />
            <SortTh label={t('xNoos.ltWith')} className="num" {...tools.thProps('lt')} /><SortTh label={t('xNoos.ltNo')} className="num" {...tools.thProps('lt2')} />
            <SortTh label="MOQ" className="num" {...tools.thProps('moq')} /><SortTh label={t('xNoos.cycle')} className="num" {...tools.thProps('cycle')} />
            <SortTh label={t('xNoos.lifecycle')} {...tools.thProps('life')} />
          </tr></thead>
          <tbody>
            {tools.shown.map((r) => (
              <tr key={r.supply_pk}><td>{r.product}</td><td>{r.scope}</td><td className="num">{n2(r.lt_with_frame_wks)}</td><td className="num">{n2(r.lt_no_frame_wks)}</td>
                <td className="num">{n0(r.moq)}</td><td className="num">{n1(r.order_cycle_wks)}</td><td>{r.lifecycle}</td></tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

/* SETTINGS AS CARDS (Max, 28/10: "chỉnh lại UI phần này") — the same panels as the dashboard's assumptions: one card per
   kind of number, a label on the left and the box with its unit on the right, Save at the foot. */
function Settings({ canEdit, onSaved }) {
  const { t } = useTranslation();
  const [s, setS] = useState(null);
  const [saved, setSaved] = useState(null);     // the values as stored — what Discard goes back to, and what "changed" is measured against
  const [history, setHistory] = useState([]);
  const [msg, setMsg] = useState(null);         // { ok, text }
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    call('GET', '/settings').then((r) => { setS(r.settings); setSaved(r.settings); setHistory(r.history || []); })
      .catch((e) => setMsg({ ok: false, text: e.message }));
  }, []);
  if (!s) return <p className="nz-hint">{msg?.text || t('xNoos.loading')}</p>;
  const flat = (x) => JSON.stringify(['targetNoos', 'targetSemi', 'zNoos', 'zSemi', 'prodLt', 'excessWeeks'].map((k) => Number(x[k]))
    .concat(ENTITIES.map((e) => Number(x.transit?.[e] ?? 0))));
  const dirty = saved && flat(s) !== flat(saved);
  const FIELD = {
    targetNoos: `${t('xNoos.setCardTarget')} · NOOS`, targetSemi: `${t('xNoos.setCardTarget')} · Semi-NOOS`,
    zNoos: `${t('xNoos.setCardZ')} · NOOS`, zSemi: `${t('xNoos.setCardZ')} · Semi-NOOS`,
    prodLt: t('xNoos.setProdLtShort'), excessWeeks: t('xNoos.setExcessShort'),
  };
  const fieldName = (f) => FIELD[f] || (f.startsWith('transit.') ? `${t('xNoos.setTransitShort')} · ${f.slice(8)}` : f);
  const when = (v) => new Intl.DateTimeFormat('en-GB', { timeZone: 'Asia/Ho_Chi_Minh', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false })
    .format(new Date(String(v).endsWith('Z') ? v : `${v}Z`));
  const row = (k, label, unit, step = '0.01') => (
    <label key={k} className="nz-set-row">
      <span>{label}</span>
      <span className="nz-set-in">
        <input type="number" min="0" step={step} value={s[k]} disabled={!canEdit} onChange={(e) => { setS({ ...s, [k]: e.target.value }); setMsg(null); }} data-nz-set={k} />
        <em>{unit}</em>
      </span>
    </label>
  );
  const save = async () => {
    setBusy(true);
    try {
      const r = await call('PUT', '/settings', s);
      setS(r.settings); setSaved(r.settings); setHistory(r.history || []);
      setMsg({ ok: true, text: t('xNoos.savedAt', { n: r.changed || 0, at: when(new Date().toISOString()) }) });
      onSaved();
    } catch (e) { setMsg({ ok: false, text: e.message }); } finally { setBusy(false); }
  };
  const wk = t('xNoos.setWeeks');
  return (
    <div className="nz-settings" data-nz-settings>
      <p className="nz-basis">{t('xNoos.settingsSub')}</p>
      <div className="nz-set-cards">
        <section className="nz-panel">
          <h2>{t('xNoos.setCardTarget')}</h2>
          {row('targetNoos', 'NOOS', wk, '0.5')}
          {row('targetSemi', 'Semi-NOOS', wk, '0.5')}
          <p className="nz-hintbox">{t('xNoos.setTargetHint')}</p>
        </section>
        <section className="nz-panel">
          <h2>{t('xNoos.setCardZ')}</h2>
          {row('zNoos', 'NOOS', 'z')}
          {row('zSemi', 'Semi-NOOS', 'z')}
          <p className="nz-hintbox">{t('xNoos.setZHint')}</p>
        </section>
        <section className="nz-panel">
          <h2>{t('xNoos.setCardLt')}</h2>
          {row('prodLt', t('xNoos.setProdLtShort'), wk, '0.5')}
          {row('excessWeeks', t('xNoos.setExcessShort'), wk, '0.5')}
          <p className="nz-hintbox">{t('xNoos.setLtHint')}</p>
        </section>
        <section className="nz-panel">
          <h2>{t('xNoos.setTransit')}</h2>
          {ENTITIES.map((e) => (
            <label key={e} className="nz-set-row">
              <span>{e}</span>
              <span className="nz-set-in">
                <input type="number" min="0" step="0.5" value={s.transit?.[e] ?? 0} disabled={!canEdit}
                  onChange={(ev) => { setS({ ...s, transit: { ...s.transit, [e]: ev.target.value } }); setMsg(null); }} data-nz-transit={e} />
                <em>{wk}</em>
              </span>
            </label>
          ))}
        </section>
      </div>
      {/* ONE BAR UNDER THE CARDS (Max, 28/10: the lone button "nhìn hơi kì") — what state the values are in on the left,
          Discard and Save on the right; Save only lights up when something was changed */}
      <div className={`nz-set-bar${dirty ? ' dirty' : ''}`} data-nz-setbar>
        <span className={`nz-set-state${msg ? (msg.ok ? ' ok' : ' bad') : ''}`} data-nz-setmsg>
          {msg ? msg.text : !canEdit ? t('xNoos.setReadOnly') : dirty ? t('xNoos.setDirty') : t('xNoos.setClean')}
        </span>
        {canEdit && (
          <span className="nz-set-btns">
            <button type="button" className="pa-btn" onClick={() => { setS(saved); setMsg(null); }} disabled={busy || !dirty} data-nz-setdiscard>{t('xNoos.setDiscard')}</button>
            <button type="button" className="pa-btn primary" onClick={save} disabled={busy || !dirty} data-nz-setsave>{t('xNoos.setSave')}</button>
          </span>
        )}
      </div>

      <section className="nz-panel nz-set-history" data-nz-history>
        <h2>{t('xNoos.setHistory')}</h2>
        {!history.length ? <p className="nz-hint">{t('xNoos.setNoHistory')}</p> : (
          <div className="pa-table-wrap nz-scroll nz-hist-scroll">
            <table className="pa-table nz-table">
              <thead><tr><th>{t('xNoos.histWhen')}</th><th>{t('xNoos.histWho')}</th><th>{t('xNoos.histField')}</th><th className="num">{t('xNoos.histWas')}</th><th className="num">{t('xNoos.histNow')}</th></tr></thead>
              <tbody>
                {history.map((h) => (
                  <tr key={h.id} data-nz-histrow>
                    <td className="nz-mono">{when(h.at)}</td><td>{h.who || '—'}</td><td>{fieldName(h.field)}</td>
                    <td className="num nz-dim">{h.was}</td><td className="num"><b>{h.now}</b></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

/* THE GUIDE (Max 01/11: every module has one). The steps describe the Dashboard tab; the tour switches back to it first,
   and the filters / assumptions / KPI steps are skipped by TourGuide if the dashboard is not drawn. */
const TOUR = [
  { selector: '.nz-page .pa-tabs', title: 'tourNoos.tabsT', body: 'tourNoos.tabsB' },
  { selector: '.nz-toolbar', title: 'tourNoos.dayT', body: 'tourNoos.dayB' },
  { selector: '.nz-actions', title: 'tourNoos.filtersT', body: 'tourNoos.filtersB' },
  { selector: '.nz-layout', title: 'tourNoos.assumpT', body: 'tourNoos.assumpB' },
  { selector: '.nz-kpis', title: 'tourNoos.kpiT', body: 'tourNoos.kpiB' },
  { selector: '.nz-subtabs', title: 'tourNoos.subsT', body: 'tourNoos.subsB' },
];

export default function Noos() {
  const { t } = useTranslation();
  const { permissions = {} } = useAuth();
  const canEdit = permissions.noos_dashboard === 'edit';
  const [tab, setTab] = useState('dashboard');
  const [days, setDays] = useState([]);
  const [day, setDay] = useState('');
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => { call('GET', '/days').then((r) => setDays(r.days)).catch((e) => setErr(e.message)); }, []);
  const load = useCallback(async (d) => {
    setBusy(true); setErr('');
    try { const r = await call('GET', `/dashboard${d ? `?day=${d}` : ''}`); setData(r); if (!d) setDay(r.day); } catch (e) { setErr(e.message); } finally { setBusy(false); }
  }, []);
  useEffect(() => { load(''); }, [load]);
  /* THE SHARE LINK (Max, 28/10: "get link html cho phần dashboard") — a secret URL to the dashboard as one HTML page,
     always the latest stock day, no sign-in (noos.js / noosHtml.js). Editors make, replace or switch it off. */
  const [share, setShare] = useState(null);
  const shareCall = async (method) => {
    setShare((x) => ({ ...(x || {}), busy: true, err: '' }));
    try { const j = await call(method, '/share'); setShare({ url: j.url, busy: false, copied: false }); }
    catch (e) { setShare((x) => ({ ...(x || {}), busy: false, err: e.message })); }
  };
  const openShare = () => { setShare({ url: null, busy: true }); shareCall('GET'); };
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(share.url); } catch (e) {
      const ta = document.createElement('textarea'); ta.value = share.url; document.body.appendChild(ta); ta.select(); document.execCommand('copy'); ta.remove();
    }
    setShare((x) => ({ ...x, copied: true }));
  };
  /* where the portal's fixed header ends, seen from the box that scrolls the page — the same measuring AI Hub does */
  const pageRef = React.useRef(null);
  useEffect(() => {
    const pg = pageRef.current; if (!pg) return undefined;
    const set = () => {
      const hd = document.querySelector('header');
      let sc = pg.parentElement;
      while (sc && !/(auto|scroll)/.test(getComputedStyle(sc).overflowY)) sc = sc.parentElement;
      if (hd) pg.style.setProperty('--nz-top', `${Math.max(0, Math.round(hd.getBoundingClientRect().bottom - (sc ? sc.getBoundingClientRect().top : 0)))}px`);
      // and how tall the sticky head is, so a scrolling table under it ends at the bottom of the window
      const h = pg.querySelector('.nz-head');
      if (h) pg.style.setProperty('--nz-head', `${Math.round(h.getBoundingClientRect().height)}px`);
    };
    set();
    const hd = document.querySelector('header');
    const ro = typeof ResizeObserver === 'function' ? new ResizeObserver(set) : null;
    if (ro) { ro.observe(pg); if (hd) ro.observe(hd); }
    return () => { if (ro) ro.disconnect(); };
  }, []);
  const pageHead = (
    <>
      <div className="mh-titlerow">
        <div className="pa-title">
          <h2>{t('xNoos.title')}</h2>
        </div>
        <ModuleHelp steps={TOUR} tourKey="noos" ready={!!data} onBeforeTour={() => setTab('dashboard')} />
      </div>
      <div className="pa-tabs view-tabs" role="tablist">
        {['dashboard', 'tiers', 'supply', 'settings'].map((x) => (
          <button key={x} type="button" className={`pa-tab ${tab === x ? 'active' : ''}`} onClick={() => setTab(x)} data-nz-tab={x}><TabIcon k={x} />{t(`xNoos.tab_${x}`)}</button>
        ))}
      </div>
      {err && <div className="pa-banner err">{err}</div>}
      {tab === 'dashboard' && (
        <div className="nz-toolbar">
          <label className="nz-day">{t('xNoos.stockDay')}
            <select value={day} onChange={(e) => { setDay(e.target.value); load(e.target.value); }} data-nz-day>
              {days.map((d) => <option key={d.day} value={d.day}>{d.day} · {d.week}</option>)}
            </select>
          </label>
          <span className="nz-grow" />
          <button type="button" className="pa-btn" onClick={() => load(day)} disabled={busy}><RefreshCw size={14} /> {t('xNoos.refresh')}</button>
          <a className="pa-btn" href={`${API}/export.xlsx${day ? `?day=${day}` : ''}`} data-nz-export><Download size={14} /> Excel</a>
          <a className="pa-btn" href={`${API}/dashboard.html${day ? `?day=${day}` : ''}`} data-nz-html><FileCode size={14} /> HTML</a>
          {canEdit && <button type="button" className="pa-btn" onClick={openShare} data-nz-share><Link2 size={14} /> {t('xNoos.shareLink')}</button>}
        </div>
      )}
    </>
  );
  return (
    <div className="pa-page">
      <Header />
      <main className="pa-main nz-page" ref={pageRef}>
        {tab === 'dashboard' && data ? <Dashboard data={data} head={pageHead} /> : (
          <div className="nz-head" data-nz-head>
            {pageHead}
            {tab === 'dashboard' && <p className="nz-hint">{t('xNoos.loading')}</p>}
          </div>
        )}
        {tab === 'tiers' && <TierList canEdit={canEdit} />}
        {tab === 'supply' && <SupplyList />}
        {tab === 'settings' && <Settings canEdit={canEdit} onSaved={() => load(day)} />}
        {share && (
          <div className="eu-overlay" role="dialog" aria-modal="true" onClick={(e) => { if (e.target === e.currentTarget) setShare(null); }}>
            <div className="eu-modal eu-share" data-nz-sharebox>
              <div className="eu-modal-head">
                <b>{t('xNoos.shareTitle')}</b>
                <button type="button" className="eu-x" onClick={() => setShare(null)} aria-label={t('xNoos.close')} title={t('xNoos.close')}><X size={18} /></button>
              </div>
              <p className="eu-sub">{t('xNoos.shareSub')}</p>
              <div className="eu-modal-body">
                {share.url ? (
                  <div className="eu-linkrow">
                    <input readOnly value={share.url} onFocus={(e) => e.target.select()} aria-label={t('xNoos.shareLink')} data-nz-shareurl />
                    <button type="button" className="pa-btn primary" onClick={copyLink}><Copy size={14} /> {share.copied ? t('xNoos.copied') : t('xNoos.copy')}</button>
                    <a className="pa-btn" href={share.url} target="_blank" rel="noreferrer">{t('xNoos.open')}</a>
                  </div>
                ) : (!share.busy && <p className="eu-nolink">{t('xNoos.noLink')}</p>)}
                <p className="eu-warn">{t('xNoos.shareWarn')}</p>
                {share.err && <div className="eu-flash bad">{share.err}</div>}
              </div>
              <div className="eu-modal-foot">
                {share.url && <button type="button" className="pa-btn" disabled={share.busy} onClick={() => shareCall('DELETE')} data-nz-shareoff>{t('xNoos.switchOff')}</button>}
                <button type="button" className="pa-btn primary" disabled={share.busy} onClick={() => shareCall('POST')} data-nz-sharemake>{share.url ? t('xNoos.replaceLink') : t('xNoos.makeLink')}</button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
